import {
  Component,
  Input,
  OnChanges,
  Renderer2,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { Logger } from '@app/core/logging';
import { GetPaidForms, FormsStatus } from '@app/+payment/models/forms.enum';
import { MatDialog } from '@angular/material/dialog';
import { CodeVerificationComponent } from '../code-verification/code-verification.component';
import { GetPaidService } from '@app/+payment/services/get-paid.service';
import { HyperwalletKycCustomer } from '@vestiaire/api-sdk-angular';
import { SuccessPopinComponent } from '../success-popin/success-popin.component';

@Component({
  selector: 'vc-forms-container',
  templateUrl: './forms-container.component.html',
  styleUrls: ['./forms-container.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FormsContainerComponent implements OnChanges {
  @Input()
  selectedForm: GetPaidForms;
  @Input()
  isWebView: boolean;
  @Input()
  customerKYC: HyperwalletKycCustomer;
  @Input()
  userIsB2C: boolean;
  @Input()
  userEmail: string;

  @Output()
  formSubmitted: EventEmitter<boolean> = new EventEmitter<boolean>();

  getPaidForms = GetPaidForms;
  widgetSelector = {
    [GetPaidForms.TRANSFERT_METHOD]: 'transfer-methods',
    [GetPaidForms.VERIFICATION]: 'users'
  };
  isLoading = false;
  error = false;
  defaultPopin = {
    height: 'auto',
    width: '88vw',
    maxWidth: '608px',
    autoFocus: false
  };
  idSuffix = '_hyper_wallet_script';

  constructor(
    private renderer: Renderer2,
    private dialogService: MatDialog,
    private getPaidService: GetPaidService,
    private logger: Logger
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const { customerKYC, selectedForm } = changes;

    if (
      customerKYC &&
      customerKYC.currentValue &&
      customerKYC.currentValue.transferMethodState === FormsStatus.AWAITING_CONFIRMATION
    ) {
      this.openCodeVerificationModal();
    } else if (selectedForm && this.widgetSelector[this.selectedForm]) {
      this.error = false;

      // remove previous script to resolve conflict between object: HWWidget from transfer method script and HWWidget from users kyc
      const previousWidgetName = this.widgetSelector[selectedForm.previousValue];

      if (previousWidgetName) {
        const previousScript = document.getElementById(`${previousWidgetName}${this.idSuffix}`);
        previousScript.parentElement.removeChild(previousScript);
      }

      // load selected widget
      this.addHyperWalletScript();
    }
  }

  openCodeVerificationModal() {
    const dialogRef = this.dialogService.open(CodeVerificationComponent, {
      ...this.defaultPopin,
      data: {
        email: this.userEmail
      }
    });

    dialogRef.afterClosed().subscribe({
      next: res => {
        if (res && res.success) {
          this.openSuccessPopin();
        } else {
          this.formSubmitted.emit(true);
        }
      }
    });
  }

  addHyperWalletScript() {
    const { env, token } = this.customerKYC;
    const widgetName = this.widgetSelector[this.selectedForm];
    const language = this.getPaidService.getUserLang();
    const version = this.selectedForm === this.getPaidForms.VERIFICATION ? '.v2_3_0' : '';

    if (widgetName) {
      this.isLoading = true;
      const scriptUrl = `https://${env}/rest/widgets/${widgetName}/${token}/${language}${version}.min.js`;
      const scriptId = `${widgetName}${this.idSuffix}`;

      this.loadScript(scriptUrl, scriptId)
        .then(_ => {
          // Initialize HyperWallet component
          // tslint:disable-next-line:no-shadowed-variable
          window['HWWidgets'].initialize((onSuccess, _) => {
            this.getAuthenticationToken(authToken => onSuccess(authToken));
          });

          if (widgetName === this.widgetSelector[GetPaidForms.TRANSFERT_METHOD]) {
            this.handleTransfertMethod();
          } else if (widgetName === this.widgetSelector[GetPaidForms.VERIFICATION]) {
            this.handleUserVerification();
          }
        })
        .catch(err => {
          this.isLoading = false;
          this.error = true;
          this.logger.error(`[GET PAID] Cannot load hyperwallet script`, err);
        });
    }
  }

  getAuthenticationToken(callback) {
    this.isLoading = true;

    return this.getPaidService
      .getAuthToken()
      .then(({ data }) => callback(data.token))
      .catch(err => {
        this.error = true;
        this.logger.error('[GET PAID] Cannot get token', err);
      })
      .finally(() => (this.isLoading = false));
  }

  /**
   * Configure HW Transfert Method Dropin Ui:
   */
  handleTransfertMethod() {
    window['HWWidgets'].transferMethods
      .configure({
        el: document.getElementById('TransferMethodUI'),
        onComplete: (trmObject, completionResult) => {
          this.isLoading = true;

          // Submit
          this.getPaidService.postTransfertMethod(trmObject.token, trmObject.transferMethodCurrency).subscribe(
            _ => {
              this.getPaidService.sendOTPCode().subscribe(
                res => {
                  this.isLoading = false;
                  this.getPaidService.updateKycCustomer({
                    ...this.customerKYC,
                    transferMethodState: 'AWAITING_CONFIRMATION'
                  });
                },
                err => {
                  this.isLoading = false;
                  this.logger.error(`[GET PAID] Cannot send OTP code`, err);
                }
              );
            },
            err => {
              this.isLoading = false;
              this.logger.error(`[GET PAID] Cannot save transferMethod`, err);
            }
          );
        },
        onLoading: isLoading => {
          this.isLoading = isLoading;
        },
        onError: err => this.handleHWErrors(err)
      })
      .display();
  }

  /**
   * Configure HW Payee Verification Dropin Ui:
   */
  handleUserVerification() {
    const widget = window['HWWidgets'].users.configure({
      el: document.getElementById('VerificationUI'),
      onError: err => this.handleHWErrors(err)
    });

    window['HWWidgets'].events.on('widget:loading:shown', () => (this.isLoading = true));

    window['HWWidgets'].events.on('widget:loading:hidden', () => (this.isLoading = false));

    window['HWWidgets'].events.on('widget:users:completed', (verificationObject, _) => {
      this.logger.debug(`[GET PAID] Verification`, verificationObject);
      this.getPaidService.getCustomer().subscribe({ next: res => this.openSuccessPopin() });
    });

    widget.display();
  }

  loadScript(scriptUrl: string, scriptId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const addHyperWalletScript = this.renderer.createElement('script');

      if (!document.getElementById(scriptId)) {
        this.renderer.setProperty(addHyperWalletScript, 'id', scriptId);
        this.renderer.setProperty(addHyperWalletScript, 'src', scriptUrl);
        this.renderer.setProperty(addHyperWalletScript, 'type', 'text/javascript');

        addHyperWalletScript.addEventListener('load', () => resolve(true));

        addHyperWalletScript.addEventListener('error', () => {
          reject(Error(`[GET PAID] Cannot load: ${scriptUrl}`));
        });

        document.getElementsByTagName('head')[0].insertBefore(addHyperWalletScript, null);
      } else {
        resolve(true);
      }
    });
  }

  handleHWErrors(err) {
    if (err && Array.isArray(err) && err.length > 0) {
      const elements = document.getElementsByName(err[0].key);
      if (elements && elements[0]) {
        elements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    if (err && err.errors) {
      const globalErrorElements = document.getElementsByClassName('hw-global-errors-list');
      if (globalErrorElements && globalErrorElements[0]) {
        globalErrorElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  onCustomerCreated(event) {
    this.openSuccessPopin();
  }

  openSuccessPopin() {
    const dialogRef = this.dialogService.open(SuccessPopinComponent, {
      ...this.defaultPopin,
      data: {
        form: this.selectedForm
      }
    });

    dialogRef.afterClosed().subscribe({
      next: _ => this.formSubmitted.emit(true)
    });
  }
}
